import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Card,
  CardContent,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  TextField,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import HomeIcon from '@mui/icons-material/Home';
import EditIcon from '@mui/icons-material/Edit';
import useAppStore from '../context/AppContext';
import { exportFiles, downloadExport, downloadAllExports } from '../services/api';

function Export() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [exportMode, setExportMode] = useState('merged');
  const [exportFormat, setExportFormat] = useState('csv');
  const [mergedFilename, setMergedFilename] = useState('');
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const {
    extractedData,
    exportId,
    exportReady,
    exportFiles: exportedFiles,
    setExportResult,
    clearFiles,
    clearExport,
  } = useAppStore();

  const totalTransactions = extractedData.reduce(
    (sum, d) => sum + (d.transactions?.length || 0),
    0
  );

  const handleExport = async () => {
    if (extractedData.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    setExporting(true);

    try {
      const statements = extractedData.map((data) => ({
        filename: data.filename, // Include original filename for individual exports
        metadata: data.metadata,
        transactions: data.transactions,
      }));

      const result = await exportFiles(statements, {
        mode: exportMode,
        format: exportFormat,
        mergedFilename: exportMode === 'merged' && mergedFilename.trim()
          ? mergedFilename.trim()
          : undefined,
      });

      if (result.success) {
        setExportResult(result.exportId, result.files);
        const formatLabel = exportFormat.toUpperCase();
        enqueueSnackbar(`${formatLabel} generated successfully!`, { variant: 'success' });
      }
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Export failed',
        { variant: 'error' }
      );
    } finally {
      setExporting(false);
    }
  };

  const handleChangeOptions = () => {
    // Clear the export result to show options again
    clearExport();
  };

  // Download a single file by index
  const handleDownloadFile = async (fileIndex) => {
    if (!exportId || !exportedFiles[fileIndex]) {
      enqueueSnackbar('File not available', { variant: 'warning' });
      return;
    }

    try {
      const file = exportedFiles[fileIndex];
      await downloadExport(exportId, file.filename, fileIndex);
      enqueueSnackbar(`Downloaded ${file.filename}`, { variant: 'success' });
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Download failed',
        { variant: 'error' }
      );
    }
  };

  // Download all files
  const handleDownloadAll = async () => {
    if (!exportId || exportedFiles.length === 0) {
      enqueueSnackbar('No export available', { variant: 'warning' });
      return;
    }

    setDownloading(true);

    try {
      if (exportedFiles.length === 1) {
        // Single file - download directly
        await downloadExport(exportId, exportedFiles[0].filename, 0);
        enqueueSnackbar('Download started!', { variant: 'success' });
      } else {
        // Multiple files - download all with delay between each
        await downloadAllExports(exportId, exportedFiles);
        enqueueSnackbar(`Downloaded ${exportedFiles.length} files!`, { variant: 'success' });
      }
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Download failed',
        { variant: 'error' }
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleStartOver = () => {
    clearFiles();
    navigate('/');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Export to QuickBooks
      </Typography>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Statements to Export
              </Typography>
              <Typography variant="h4">{extractedData.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Transactions
              </Typography>
              <Typography variant="h4">{totalTransactions}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Options */}
      {!exportReady && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Export Options
          </Typography>

          <Grid container spacing={3}>
            {/* Export Mode */}
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Export Mode</FormLabel>
                <RadioGroup
                  value={exportMode}
                  onChange={(e) => setExportMode(e.target.value)}
                >
                  <FormControlLabel
                    value="merged"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Merged File</Typography>
                        <Typography variant="body2" color="text.secondary">
                          All transactions combined into a single file
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="individual"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Individual Files</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Separate file for each statement (uses original filename)
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Export Format */}
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend">File Format</FormLabel>
                <RadioGroup
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                >
                  <FormControlLabel
                    value="csv"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">CSV</Typography>
                        <Typography variant="body2" color="text.secondary">
                          QuickBooks Online compatible format
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="xlsx"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Excel (XLSX)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Standard Excel spreadsheet format
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Custom Filename for Merged */}
            {exportMode === 'merged' && (
              <Grid item xs={12}>
                <TextField
                  label="Custom Filename (optional)"
                  value={mergedFilename}
                  onChange={(e) => setMergedFilename(e.target.value)}
                  placeholder={`QBO_Merged_${new Date().toISOString().split('T')[0]}`}
                  fullWidth
                  helperText={`Leave empty for default name. Extension (.${exportFormat}) will be added automatically.`}
                  InputProps={{
                    endAdornment: mergedFilename && (
                      <Typography variant="body2" color="text.secondary">
                        .{exportFormat}
                      </Typography>
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>

          <Alert severity="info" sx={{ my: 3 }}>
            {exportFormat === 'csv' ? (
              <>
                The CSV will be formatted for QuickBooks Online with:
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Date format: YYYY-MM-DD</li>
                  <li>Expenses as negative amounts</li>
                  <li>UTF-8 encoding with Excel compatibility</li>
                </ul>
              </>
            ) : (
              <>
                The Excel file will include:
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Formatted columns with proper headers</li>
                  <li>Date cells formatted as dates</li>
                  <li>Amount cells formatted as currency</li>
                </ul>
              </>
            )}
          </Alert>

          <Button
            variant="contained"
            size="large"
            onClick={handleExport}
            disabled={exporting || extractedData.length === 0}
            startIcon={exporting ? <CircularProgress size={20} /> : (exportFormat === 'csv' ? <DescriptionIcon /> : <TableChartIcon />)}
          >
            {exporting ? 'Generating...' : `Generate ${exportFormat.toUpperCase()}`}
          </Button>
        </Paper>
      )}

      {/* Export Result */}
      {exportReady && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            Your file{exportedFiles.length > 1 ? 's are' : ' is'} ready for download!
          </Alert>

          <Typography variant="h6" gutterBottom>
            Generated Files
          </Typography>

          <List>
            {exportedFiles.map((file, idx) => (
              <ListItem
                key={idx}
                secondaryAction={
                  <Tooltip title={`Download ${file.filename}`}>
                    <IconButton
                      edge="end"
                      onClick={() => handleDownloadFile(idx)}
                      color="primary"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemIcon>
                  {file.filename.endsWith('.xlsx') ? (
                    <TableChartIcon color="success" />
                  ) : (
                    <DescriptionIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={file.filename}
                  secondary={`${file.transactionCount} transactions • ${(file.size / 1024).toFixed(1)} KB`}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleDownloadAll}
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            >
              {downloading
                ? 'Downloading...'
                : exportedFiles.length > 1
                  ? `Download All (${exportedFiles.length} files)`
                  : 'Download File'}
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={handleChangeOptions}
              startIcon={<EditIcon />}
            >
              Change Options
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={handleStartOver}
              startIcon={<HomeIcon />}
            >
              Process More Files
            </Button>
          </Box>
        </Paper>
      )}

      {/* Navigation */}
      {!exportReady && (
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/preview')}
        >
          Back to Preview
        </Button>
      )}
    </Box>
  );
}

export default Export;
